import React, { useEffect, useMemo, useState } from "react";

import {
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
  Collapse,
  Slider,
  Chip,
  Divider,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  Button,
  InputAdornment
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import {
  useForm,
  Controller
} from "react-hook-form";

import { useEmployees } from "../../context/EmployeesContext";
import { useUserRole } from "../../context/UserRoleContext";
import { initialsOf } from "../../utils/commentUtils";
import { avatarColorOf } from "../../utils/avatarUtils";
import AppSelect from "../../ui/AppSelect";
import AppDatePicker from "../../ui/AppDatePicker";
import RichTextEditor from "../../ui/RichTextEditor";
import CapacitySlider from "../../ui/CapacitySlider";

import {
  getProjects
} from "../../services/projectService";

import ResourceCalendar from "./ResourceCalendar";

import CapacityCalendar from "./CapacityCalendar";

// Match the query against the full name or any individual word's start (e.g.
// "Shah" matches "Bhumit Shah"), normalizing whitespace/case for reliability
// against synced data that may contain stray double-spaces.
const normalizeName = (value) => (value || "").toLowerCase().replace(/\s+/g, " ").trim();

// react-select's filterOption signature: (option, rawInput) => boolean.
const employeeSelectFilterOption = (option, rawInput) => {
  const normalizedQuery = normalizeName(rawInput);

  if (!normalizedQuery) {
    return true;
  }

  const name = normalizeName(option.label);
  return name.startsWith(normalizedQuery) || name.split(" ").some((word) => word.startsWith(normalizedQuery));
};

const PROJECT_TYPE_OPTIONS = [
  { value: "Internal", label: "Internal" },
  { value: "Client", label: "Client" },
  { value: "Billed", label: "Billed" },
  { value: "Branding & PR", label: "Branding & PR" },
  { value: "Delivery - Billed", label: "Delivery - Billed" },
  { value: "Delivery - Investment", label: "Delivery - Investment" },
  { value: "Pre-Sales", label: "Pre-Sales" },
  { value: "Sales (existing Customers)", label: "Sales (existing Customers)" },
  { value: "UnBilled", label: "UnBilled" },
  { value: "UnBilled -PoC", label: "UnBilled -PoC" },
  { value: "UnBilled-Internal", label: "UnBilled-Internal" },
  { value: "Delivery - Billed", label: "Delivery - Billed" },
  { value: "Delivery - Non-Billed", label: "Delivery - Non-Billed" }
];

// Resource categories offered in the "Add Resource" flow; "Other" reveals a free-text field.
const RESOURCE_TYPE_OPTIONS = [
  "Frontend",
  "Backend",
  "Full Stack",
  "Web Architect",
  "DE",
  "DS",
  "Project Manager",
  "QA",
  "DevOps",
  "UI/UX",
  "Business Analyst",
  "Other"
];
const UNASSIGNED_RESOURCE_TYPE = "Unassigned";
// Sentinel for the top-level "Add Resource" flow (creating a new requirement row).
const NEW_RESOURCE_KEY = "__new_resource__";
const SENIORITY_OPTIONS = ["Junior", "Mid", "Senior"];

// A resource requirement is identified by its type + seniority combo (e.g. two
// separate "Data Engineer" rows for Senior vs Junior).
const resourceKeyOf = (resourceType, seniority) => (seniority ? `${resourceType}__${seniority}` : resourceType);
const parseResourceKey = (key) => {
  const idx = key.indexOf("__");
  return idx === -1 ? { resourceType: key, seniority: "" } : { resourceType: key.slice(0, idx), seniority: key.slice(idx + 2) };
};
// Persisted on ProjectEmployee.resource_type as a single human-readable string, e.g. "Data Engineer (Senior)".
const resourceTypeLabelOf = (resourceType, seniority) => (seniority ? `${resourceType} (${seniority})` : resourceType);
const parseResourceTypeLabel = (label) => {
  const match = label.match(/^(.*) \(([^)]+)\)$/);
  return match ? { resourceType: match[1], seniority: match[2] } : { resourceType: label, seniority: "" };
};

function localStr(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function buildWeeks(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const weeks = [];
  const monday = new Date(start);
  const day = monday.getDay();
  monday.setDate(monday.getDate() + (day === 0 ? -6 : 1 - day));
  while (monday <= end) {
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    weeks.push({ key: localStr(monday), start: new Date(monday), end: sunday });
    monday.setDate(monday.getDate() + 7);
  }
  return weeks;
}

function fmtDate(date) {
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function capColor(v) {
  if (v >= 100) return "#ef4444";
  if (v >= 75) return "#f97316";
  if (v >= 50) return "#eab308";
  return "#22c55e";
}

function ProjectForm({
  project,
  onSubmit
}) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors }
  } = useForm();

  const { employees } = useEmployees();
  const { employee: currentEmployee } = useUserRole();
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeeCapacities, setEmployeeCapacities] = useState({});
  const [projectManagerId, setProjectManagerId] = useState(project?.project_manager_id || "");
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const [dateConflicts, setDateConflicts] = useState([]);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [collapsedEmployees, setCollapsedEmployees] = useState({});
  const [employeeWeeklyCapacities, setEmployeeWeeklyCapacities] = useState({});
  // employeeId -> { resourceType, seniority } (role this person fills on THIS project)
  const [employeeResourceInfo, setEmployeeResourceInfo] = useState({});
  // Planned resource requirements: [{ id, resourceType, seniority, capacityAllocation, count, comment }]
  const [resourceRequirements, setResourceRequirements] = useState([]);
  // null | NEW_RESOURCE_KEY (creating a new requirement row) | a requirement/ad-hoc group id (adding an employee to it)
  const [addingResourceFor, setAddingResourceFor] = useState(null);
  const [pendingEmployeeId, setPendingEmployeeId] = useState("");
  const [pendingResourceType, setPendingResourceType] = useState("");
  const [pendingCustomResourceType, setPendingCustomResourceType] = useState("");
  const [pendingCapacity, setPendingCapacity] = useState(100);
  const [pendingSeniority, setPendingSeniority] = useState("");
  const [pendingCapacityAllocation, setPendingCapacityAllocation] = useState(0);
  const [pendingCount, setPendingCount] = useState(1);
  const [pendingComment, setPendingComment] = useState("");

  const toggleEmployee = (empId) =>
    setCollapsedEmployees((prev) => ({ ...prev, [empId]: !prev[empId] }));

  const startDate = watch("start_date", project?.start_date || "");
  const endDate = watch("end_date", project?.end_date || "");
  const projectType = watch("project_type", project?.project_type || "Internal");

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const weeks = useMemo(() => buildWeeks(startDate, endDate), [startDate, endDate]);

  const sortedEmployees = useMemo(
    () => [...employees].sort((a, b) => a.employee_name.localeCompare(b.employee_name)),
    [employees]
  );

  // Group selected employees under their matching resource requirement row (by type+seniority),
  // falling back to an ad-hoc group (legacy data with no matching requirement) or "Unassigned".
  const resourceGroups = useMemo(() => {
    const employeesByKey = {};
    const unassigned = [];
    selectedEmployeeIds.forEach((empId) => {
      const info = employeeResourceInfo[empId];
      if (!info || !info.resourceType) {
        unassigned.push(empId);
        return;
      }
      const key = resourceKeyOf(info.resourceType, info.seniority);
      if (!employeesByKey[key]) employeesByKey[key] = [];
      employeesByKey[key].push(empId);
    });

    const requirementKeys = new Set();
    const requirementGroups = resourceRequirements.map((req) => {
      const key = resourceKeyOf(req.resourceType, req.seniority);
      requirementKeys.add(key);
      return { id: req.id, requirement: req, key, employeeIds: employeesByKey[key] || [] };
    });

    const adHocGroups = Object.keys(employeesByKey)
      .filter((key) => !requirementKeys.has(key))
      .sort()
      .map((key) => ({ id: key, requirement: null, key, employeeIds: employeesByKey[key] }));

    const unassignedGroup = unassigned.length
      ? [{ id: UNASSIGNED_RESOURCE_TYPE, requirement: null, key: UNASSIGNED_RESOURCE_TYPE, employeeIds: unassigned }]
      : [];

    return [...requirementGroups, ...adHocGroups, ...unassignedGroup];
  }, [selectedEmployeeIds, employeeResourceInfo, resourceRequirements]);

  useEffect(() => {
    if (project) {
      const ids = Array.isArray(project.assigned_to)
        ? project.assigned_to.map((a) => (typeof a === "object" ? a.employee_id : a))
        : (project.assigned_to ? [project.assigned_to] : []);
      const caps = {};
      const weeklyCaps = {};
      const resourceInfo = {};
      if (Array.isArray(project.assigned_to)) {
        project.assigned_to.forEach((a) => {
          if (typeof a === "object") {
            caps[a.employee_id] = a.capacity_percent || 0;
            if (a.weekly_capacity) weeklyCaps[a.employee_id] = a.weekly_capacity;
            resourceInfo[a.employee_id] = a.resource_type ? parseResourceTypeLabel(a.resource_type) : null;
          }
        });
      }
      reset({
        project_name: project.project_name,
        description: project.description || "",
        assigned_to: ids,
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        project_type: project.project_type || "Internal",
        client_name: project.client_name || "",
        po_number: project.po_number || "",
        project_manager_id: project.project_manager_id || "",
      });
      setSelectedEmployeeIds(ids);
      setEmployeeCapacities(caps);
      setEmployeeWeeklyCapacities(weeklyCaps);
      setEmployeeResourceInfo(resourceInfo);
      setResourceRequirements(
        Array.isArray(project.planned_resource_types)
          ? project.planned_resource_types.map((r, idx) => ({
              id: `req-${idx}-${Date.now()}`,
              resourceType: r.resource_type,
              seniority: r.seniority || "",
              capacityAllocation: r.capacity_allocation ?? "",
              count: r.count ?? "",
              comment: r.comment || "",
            }))
          : []
      );
      setProjectManagerId(project.project_manager_id || "");
    }
    else {
      // New project: default Report To to the signed-in user instead of leaving it blank.
      const defaultManagerId = currentEmployee?.employee_id || "";
      reset({
        project_name: "",
        description: "",
        assigned_to: [],
        start_date: "",
        end_date: "",
        project_type: "Internal",
        client_name: "",
        po_number: "",
        project_manager_id: defaultManagerId,
      });
      setEmployeeCapacities({});
      setEmployeeWeeklyCapacities({});
      setEmployeeResourceInfo({});
      setResourceRequirements([]);
      setProjectManagerId(defaultManagerId);
    }
  }, [project, reset, currentEmployee]);

  const handleEmployeeChange = async (ids) => {
    setSelectedEmployeeIds(ids);
    setValue("assigned_to", ids, { shouldValidate: true });
    setDateConflicts([]);

    // Track dates where an employee is already fully committed (300%) across
    // other projects, purely as an informational warning (not a hard block) -
    // employees can legitimately be double/triple booked.
    const allBooked = [];
    for (const employeeId of ids) {
      try {
        const projects = await getProjects("", employeeId);
        projects.forEach((proj) => {
          if (proj.capacity_percent >= 300 && proj.start_date && proj.end_date && proj.project_id !== project?.project_id) {
            const start = new Date(proj.start_date);
            const end = new Date(proj.end_date);
            let current = new Date(start);
            while (current <= end) {
              const dateStr = current.toISOString().split("T")[0];
              if (!allBooked.includes(dateStr)) allBooked.push(dateStr);
              current.setDate(current.getDate() + 1);
            }
          }
        });
      } catch (error) {
        console.error("Error loading capacity dates:", error);
      }
    }
    setFullyBookedDates(allBooked);
  };

  useEffect(() => {
    if (startDate && endDate && fullyBookedDates.length > 0) {
      const conflicts = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      let current = new Date(start);

      while (current <= end) {
        const dateStr = current.toISOString().split("T")[0];
        if (fullyBookedDates.includes(dateStr)) {
          conflicts.push(dateStr);
        }
        current.setDate(current.getDate() + 1);
      }

      setDateConflicts(conflicts);
    } else {
      setDateConflicts([]);
    }
  }, [startDate, endDate, fullyBookedDates]);

  const resetAddResourceForm = () => {
    setAddingResourceFor(null);
    setPendingEmployeeId("");
    setPendingResourceType("");
    setPendingCustomResourceType("");
    setPendingCapacity(100);
    setPendingSeniority("");
    setPendingCapacityAllocation(0);
    setPendingCount(1);
    setPendingComment("");
  };

  // Opens the inline row for creating a brand-new resource requirement (type still to be chosen).
  const startAddResource = () => {
    resetAddResourceForm();
    setAddingResourceFor(NEW_RESOURCE_KEY);
  };

  // Opens the inline row for adding a named employee to an existing requirement/ad-hoc group.
  const startAddEmployeeFor = (groupId) => {
    resetAddResourceForm();
    setAddingResourceFor(groupId);
  };

  const updateRequirementField = (reqId, field, value) => {
    setResourceRequirements((prev) => prev.map((r) => (r.id === reqId ? { ...r, [field]: value } : r)));
  };

  // Editing Resource Type/Seniority changes the grouping key, so keep any employees
  // already assigned to this row in sync instead of silently orphaning them.
  const updateRequirementIdentity = (reqId, field, value) => {
    setResourceRequirements((prev) => {
      const current = prev.find((r) => r.id === reqId);
      if (!current) return prev;
      const previousKey = resourceKeyOf(current.resourceType, current.seniority);
      const updated = { ...current, [field]: value };

      setEmployeeResourceInfo((prevInfo) => {
        const nextInfo = { ...prevInfo };
        Object.keys(nextInfo).forEach((empId) => {
          const info = nextInfo[empId];
          if (info && resourceKeyOf(info.resourceType, info.seniority) === previousKey) {
            nextInfo[empId] = { resourceType: updated.resourceType, seniority: updated.seniority };
          }
        });
        return nextInfo;
      });

      return prev.map((r) => (r.id === reqId ? updated : r));
    });
  };

  const confirmAddResource = () => {
    const capacityValue = Math.max(0, Math.min(300, Number(pendingCapacity) || 0));

    if (addingResourceFor === NEW_RESOURCE_KEY) {
      const resolvedType = pendingResourceType === "Other"
        ? pendingCustomResourceType.trim()
        : pendingResourceType;

      const newRequirement = {
        id: `req-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        resourceType: resolvedType,
        seniority: pendingSeniority,
        capacityAllocation: pendingCapacityAllocation,
        count: pendingCount,
        comment: pendingComment,
      };
      setResourceRequirements((prev) => [...prev, newRequirement]);

      if (pendingEmployeeId) {
        handleEmployeeChange([...selectedEmployeeIds, pendingEmployeeId]);
        setEmployeeResourceInfo((prev) => ({ ...prev, [pendingEmployeeId]: { resourceType: resolvedType, seniority: pendingSeniority } }));
        setEmployeeCapacities((prev) => ({ ...prev, [pendingEmployeeId]: capacityValue }));
      }
    } else if (pendingEmployeeId) {
      const requirement = resourceRequirements.find((r) => r.id === addingResourceFor);
      const info = requirement
        ? { resourceType: requirement.resourceType, seniority: requirement.seniority }
        : parseResourceKey(addingResourceFor);

      handleEmployeeChange([...selectedEmployeeIds, pendingEmployeeId]);
      setEmployeeResourceInfo((prev) => ({ ...prev, [pendingEmployeeId]: info }));
      setEmployeeCapacities((prev) => ({ ...prev, [pendingEmployeeId]: capacityValue }));
    }

    resetAddResourceForm();
  };

  return (
    <form
      id="project-form"
      style={{ height: "100%" }}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit((data) => {
          const payload = {
            ...data,
            assigned_to: selectedEmployeeIds.map((id) => {
              const info = employeeResourceInfo[id];
              return {
                employee_id: id,
                capacity_percent: employeeCapacities[id] || 0,
                weekly_capacity: employeeWeeklyCapacities[id] || {},
                resource_type: info && info.resourceType ? resourceTypeLabelOf(info.resourceType, info.seniority) : null,
              };
            }),
            planned_resource_types: resourceRequirements.map((r) => ({
              resource_type: r.resourceType,
              seniority: r.seniority || null,
              capacity_allocation: r.capacityAllocation === "" || r.capacityAllocation === null || r.capacityAllocation === undefined
                ? null
                : Number(r.capacityAllocation),
              count: r.count === "" || r.count === null || r.count === undefined ? null : Number(r.count),
              comment: r.comment ? r.comment.trim() || null : null,
            })),
            start_date: data.start_date || null,
            end_date: data.end_date || null,
            project_manager_id: projectManagerId ? Number(projectManagerId) : null,
            client_name: data.project_type !== "Internal" ? data.client_name : null,
            po_number: data.po_number || null,
          };
          onSubmit(payload);
        })(e);
      }}
    >
      {/* hidden field so react-hook-form tracks assigned_to */}
      <input type="hidden" {...register("assigned_to")} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 4fr) minmax(0, 1fr)" },
            gap: 3
          }}
        >
          {/* Left column: Project Name + Description */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Project Name <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Enter project name"
                error={Boolean(errors.project_name)}
                helperText={errors.project_name ? "Project name is required" : ""}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "background.paper"
                  },
                  "& .MuiFormHelperText-root": {
                    minHeight: 20,
                    mt: 0.7
                  }
                }}
                {...register("project_name", { required: true })}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Description
              </Typography>

              <Controller
                name="description"
                control={control}
                defaultValue={project?.description || ""}
                render={({ field }) => (
                  <RichTextEditor
                    className="project-description-editor"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter project description"
                    minHeight={310}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Right column: Details (Type, Client Name, Project Manager, Assign To, Dates) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2, height: "100%" }}>
            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1 }}>
              <Box
                onClick={() => setDetailsOpen((prev) => !prev)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Details
                </Typography>
                <IconButton size="small">
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                  />
                </IconButton>
              </Box>

              <Collapse in={detailsOpen} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Project Type <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                    </Typography>

                    <AppSelect
                      options={PROJECT_TYPE_OPTIONS}
                      value={projectType}
                      onChange={(value) => setValue("project_type", value, { shouldValidate: true })}
                    />
                  </Box>

                  {projectType !== "Internal" && (
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                        Client Name
                      </Typography>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter client name"
                        error={Boolean(errors.client_name)}
                        helperText={errors.client_name ? "Client name is required" : ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper"
                          },
                          "& .MuiFormHelperText-root": {
                            minHeight: 20,
                            mt: 0.7
                          }
                        }}
                        {...register("client_name", { required: projectType === "Client" })}
                      />
                    </Box>
                  )}

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      PO Number
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter PO number (optional)"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "background.paper"
                        }
                      }}
                      {...register("po_number")}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                     Report To <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                    </Typography>

                    <AppSelect
                      options={sortedEmployees.map((emp) => ({ value: emp.employee_id, label: emp.employee_name, avatar: emp.profile_picture }))}
                      value={projectManagerId}
                      onChange={(value) => setProjectManagerId(value)}
                      filterOption={employeeSelectFilterOption}
                      isClearable
                      withAvatar
                      placeholder="Search and select project manager"
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Project Start Date
                    </Typography>

                    <AppDatePicker
                      selected={startDate ? new Date(startDate) : null}
                      onChange={(date) => {
                        setValue("start_date", date ? date.toISOString().split("T")[0] : "", { shouldValidate: true });
                        setStartDateError("");
                      }}
                      placeholder="Select start date"
                      popperPlacement="bottom-start"
                      error={Boolean(startDateError)}
                      helperText={startDateError}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Project End Date
                    </Typography>

                    <AppDatePicker
                      selected={endDate ? new Date(endDate) : null}
                      onChange={(date) => {
                        setValue("end_date", date ? date.toISOString().split("T")[0] : "", { shouldValidate: true });
                        setEndDateError("");
                      }}
                      minDate={startDate ? new Date(startDate) : null}
                      placeholder="Select end date"
                      popperPlacement="bottom-end"
                      error={Boolean(endDateError)}
                      helperText={endDateError}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>

        {/* Resources table: pick a role (Frontend, Backend, DE, DS, ...), then add employees under it */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.2 }}>
            <Typography sx={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>
              Resources
            </Typography>

            {!addingResourceFor && (
              <Button
                size="small"
                startIcon={<AddRoundedIcon fontSize="small" />}
                onClick={startAddResource}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add Resource
              </Button>
            )}
          </Box>

          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: "background.default", fontWeight: 700, fontSize: 12, color: "text.secondary" } }}>
                  <TableCell sx={{ width: 200 }}>Resource Type</TableCell>
                  <TableCell sx={{ width: 200 }}>Seniority</TableCell>
                  <TableCell sx={{ width: 200 }}>Capacity Allocation</TableCell>
                  <TableCell sx={{ width: 90 }}>Count</TableCell>
                  <TableCell sx={{ width: 320 }}>Comments/Recommendations</TableCell>
                  <TableCell align="right" sx={{ width: 190 }} />
                </TableRow>
              </TableHead>

              <TableBody>
                {resourceGroups.map((group) => {
                  const { resourceType: groupType, seniority: groupSeniority } = group.requirement
                    ? group.requirement
                    : (group.key === UNASSIGNED_RESOURCE_TYPE ? { resourceType: UNASSIGNED_RESOURCE_TYPE, seniority: "" } : parseResourceKey(group.key));

                  return (
                    <React.Fragment key={group.id}>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "background.default" }}>
                          {group.requirement ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <TextField
                                size="small"
                                value={group.requirement.resourceType}
                                onChange={(e) => updateRequirementIdentity(group.id, "resourceType", e.target.value)}
                                sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "background.paper" } }}
                              />
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                ({group.employeeIds.length})
                              </Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                              {groupType} ({group.employeeIds.length})
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ bgcolor: "background.default" }}>
                          {group.requirement ? (
                            <Box sx={{ width: 150 }}>
                              <AppSelect
                                options={SENIORITY_OPTIONS.map((s) => ({ value: s, label: s }))}
                                value={group.requirement.seniority}
                                onChange={(value) => updateRequirementIdentity(group.id, "seniority", value)}
                                isClearable
                                placeholder="Select Seniority"
                              />
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                              {groupSeniority || "—"}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ bgcolor: "background.default" }}>
                          {group.requirement && (
                            group.employeeIds.length === 0 ? (
                              <TextField
                                size="small"
                                type="number"
                                value={group.requirement.capacityAllocation}
                                onChange={(e) => updateRequirementField(group.id, "capacityAllocation", e.target.value)}
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                inputProps={{ min: 0, max: 300, step: 5 }}
                                sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "background.paper" } }}
                              />
                            ) : (
                              // Once real employees are assigned, their own % below is the source of truth —
                              // show the total allocated instead of a separate (and confusingly empty) planning field.
                              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                                {group.employeeIds.reduce((sum, empId) => sum + (employeeCapacities[empId] || 0), 0)}% allocated
                              </Typography>
                            )
                          )}
                        </TableCell>
                        <TableCell sx={{ bgcolor: "background.default" }}>
                          {group.requirement && (
                            <TextField
                              size="small"
                              type="number"
                              value={group.requirement.count}
                              onChange={(e) => updateRequirementField(group.id, "count", e.target.value)}
                              inputProps={{ min: 0, step: 1 }}
                              sx={{ width: 70, "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "background.paper" } }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ bgcolor: "background.default" }}>
                          {group.requirement && (
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              minRows={2}
                              placeholder="Comments/Recommendations"
                              value={group.requirement.comment}
                              onChange={(e) => updateRequirementField(group.id, "comment", e.target.value)}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "background.paper" } }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ bgcolor: "background.default" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75 }}>
                            {!addingResourceFor && group.key !== UNASSIGNED_RESOURCE_TYPE && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => startAddEmployeeFor(group.id)}
                                sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
                              >
                                Add
                              </Button>
                            )}

                            {group.requirement && group.employeeIds.length === 0 && (
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setResourceRequirements((prev) => prev.filter((r) => r.id !== group.id))}
                                sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
                              >
                                Delete
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>

                      {group.employeeIds.map((empId) => {
                        const emp = employees.find((e) => e.employee_id === empId);
                        const name = emp ? emp.employee_name : `Employee ${empId}`;

                        return (
                          <TableRow key={empId}>
                            <TableCell sx={{ pl: 4 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  src={emp?.profile_picture || undefined}
                                  sx={{ width: 24, height: 24, fontSize: 10, bgcolor: avatarColorOf(name) }}
                                >
                                  {initialsOf(name)}
                                </Avatar>
                                <Typography fontSize={13} fontWeight={600}>{name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell />
                            <TableCell colSpan={2}>
                              <TextField
                                size="small"
                                type="number"
                                value={employeeCapacities[empId] ?? 0}
                                onChange={(e) => {
                                  const val = Math.max(0, Math.min(300, Number(e.target.value) || 0));
                                  setEmployeeCapacities((prev) => ({ ...prev, [empId]: val }));
                                }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                                }}
                                inputProps={{ min: 0, max: 300, step: 5 }}
                                sx={{ width: 120, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                              />
                            </TableCell>
                            <TableCell />
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => {
                                  handleEmployeeChange(selectedEmployeeIds.filter((id) => id !== empId));
                                  setEmployeeResourceInfo((prev) => {
                                    const next = { ...prev };
                                    delete next[empId];
                                    return next;
                                  });
                                }}
                                sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {addingResourceFor === group.id && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <AppSelect
                                  options={sortedEmployees
                                    .filter((emp) => !selectedEmployeeIds.includes(emp.employee_id))
                                    .map((emp) => ({ value: emp.employee_id, label: emp.employee_name, avatar: emp.profile_picture }))}
                                  value={pendingEmployeeId}
                                  onChange={(value) => setPendingEmployeeId(value)}
                                  filterOption={employeeSelectFilterOption}
                                  withAvatar
                                  isClearable
                                  autoFocus
                                  placeholder={`Search and select an employee for ${groupType}`}
                                />
                              </Box>
                              <Box sx={{ width: 110, flexShrink: 0 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={pendingCapacity}
                                  onChange={(e) => setPendingCapacity(e.target.value)}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                                  }}
                                  inputProps={{ min: 0, max: 300, step: 5 }}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                                />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" gap={0.5} justifyContent="flex-end">
                              <IconButton size="small" color="primary" disabled={!pendingEmployeeId} onClick={confirmAddResource}>
                                <CheckRoundedIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={resetAddResourceForm}>
                                <CloseRoundedIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

                {addingResourceFor === NEW_RESOURCE_KEY && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Box sx={{ width: 170 }}>
                          <AppSelect
                            options={RESOURCE_TYPE_OPTIONS.map((type) => ({ value: type, label: type }))}
                            value={pendingResourceType}
                            onChange={(value) => setPendingResourceType(value)}
                            placeholder="Resource type"
                          />
                        </Box>

                        {pendingResourceType === "Other" && (
                          <Box sx={{ width: 180 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Custom resource type"
                              value={pendingCustomResourceType}
                              onChange={(e) => setPendingCustomResourceType(e.target.value)}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                          </Box>
                        )}

                        <Box sx={{ width: 170 }}>
                          <AppSelect
                            options={SENIORITY_OPTIONS.map((s) => ({ value: s, label: s }))}
                            value={pendingSeniority}
                            onChange={(value) => setPendingSeniority(value)}
                            isClearable
                            placeholder="Seniority (optional)"
                          />
                        </Box>

                        <Box sx={{ width: 150 }}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Capacity"
                            value={pendingCapacityAllocation}
                            onChange={(e) => setPendingCapacityAllocation(e.target.value)}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            inputProps={{ min: 0, max: 300, step: 5 }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                          />
                        </Box>

                        <Box sx={{ width: 90 }}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Count"
                            value={pendingCount}
                            onChange={(e) => setPendingCount(e.target.value)}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                          />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 220 }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            placeholder="Comments/Recommendations (optional)"
                            value={pendingComment}
                            onChange={(e) => setPendingComment(e.target.value)}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                          />
                        </Box>
                      </Box>

                      {(pendingResourceType && pendingResourceType !== "Other") ||
                      (pendingResourceType === "Other" && pendingCustomResourceType.trim()) ? (
                        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <AppSelect
                              options={sortedEmployees
                                .filter((emp) => !selectedEmployeeIds.includes(emp.employee_id))
                                .map((emp) => ({ value: emp.employee_id, label: emp.employee_name, avatar: emp.profile_picture }))}
                              value={pendingEmployeeId}
                              onChange={(value) => setPendingEmployeeId(value)}
                              filterOption={employeeSelectFilterOption}
                              withAvatar
                              isClearable
                              placeholder="Search and select an employee (optional)"
                            />
                          </Box>
                          <Box sx={{ width: 110, flexShrink: 0 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={pendingCapacity}
                              onChange={(e) => setPendingCapacity(e.target.value)}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                              }}
                              inputProps={{ min: 0, max: 300, step: 5 }}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                            />
                          </Box>
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={
                            !pendingResourceType ||
                            (pendingResourceType === "Other" && !pendingCustomResourceType.trim())
                          }
                          onClick={confirmAddResource}
                        >
                          <CheckRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={resetAddResourceForm}>
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {resourceGroups.length === 0 && !addingResourceFor && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography fontSize={13} color="text.secondary" sx={{ py: 1.5, textAlign: "center" }}>
                        No resources added yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Full-width sections below: capacity calendars + resource allocation */}
        {selectedEmployeeIds.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                color: "#475569",
                fontWeight: 600,
                mb: 1.5
              }}
            >
              Current Capacity & Calendar
            </Typography>
            {selectedEmployeeIds.map((empId) => {
              const emp = employees.find((e) => e.employee_id === empId);
              const isOpen = !collapsedEmployees[empId];
              return (
                <Box
                  key={empId}
                  mb={2}
                  sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.default", overflow: "hidden" }}
                >
                  <Box
                    onClick={() => toggleEmployee(empId)}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.2, cursor: "pointer", bgcolor: "background.paper" }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
                      {emp ? emp.employee_name : `Employee ${empId}`}
                    </Typography>
                    <IconButton size="small">
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                      />
                    </IconButton>
                  </Box>

                  <Collapse in={isOpen}>
                    <Box sx={{ p: 2 }}>
                      <Box mb={2}>
                        <ResourceCalendar employeeId={empId} />
                      </Box>
                      <CapacityCalendar employeeId={empId} />
                      <Box mt={2}>
                        <CapacitySlider
                          name={emp ? emp.employee_name : `Employee ${empId}`}
                          avatarUrl={emp?.profile_picture}
                          value={employeeCapacities[empId] || 0}
                          onChange={(e, val) => setEmployeeCapacities((prev) => ({ ...prev, [empId]: val }))}
                        />
                      </Box>

                      {weeks.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }}>
                            <Typography fontSize={12} color="text.secondary">Week-by-week overrides</Typography>
                          </Divider>

                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 380, overflowY: "auto", pr: 0.5 }}>
                            {weeks.map((week) => {
                              const globalCap = employeeCapacities[empId] || 0;
                              const cap = employeeWeeklyCapacities[empId]?.[week.key] ?? globalCap;
                              const hasOverride = employeeWeeklyCapacities[empId]?.[week.key] !== undefined;
                              const completed = week.end < today;
                              const isCurrent = week.start <= today && today <= week.end;

                              return (
                                <Box
                                  key={week.key}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: isCurrent ? "primary.main" : "divider",
                                    bgcolor: completed ? "action.hover" : "background.paper",
                                    opacity: completed ? 0.7 : 1,
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={completed ? 0 : 1}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Tooltip title={completed ? "Completed — read only" : "Editable"}>
                                        {completed
                                          ? <LockRoundedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                                          : <LockOpenRoundedIcon sx={{ fontSize: 14, color: isCurrent ? "primary.main" : "text.secondary" }} />
                                        }
                                      </Tooltip>
                                      <Typography fontSize={12} fontWeight={600} color={completed ? "text.disabled" : "text.primary"}>
                                        {fmtDate(week.start)} – {fmtDate(week.end)}
                                      </Typography>
                                      {isCurrent && (
                                        <Chip label="This Week" size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />
                                      )}
                                      {hasOverride && !completed && (
                                        <Chip label="Custom" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#f0fdf4", color: "#16a34a" }} />
                                      )}
                                    </Box>
                                    <Chip
                                      label={`${cap}%`}
                                      size="small"
                                      sx={{
                                        background: completed ? "#e2e8f0" : capColor(cap),
                                        color: completed ? "#64748b" : "#fff",
                                        fontWeight: 700,
                                      }}
                                    />
                                  </Box>

                                  {!completed && (
                                    <Slider
                                      value={cap}
                                      onChange={(_, v) =>
                                        setEmployeeWeeklyCapacities((prev) => ({
                                          ...prev,
                                          [empId]: { ...(prev[empId] || {}), [week.key]: v },
                                        }))
                                      }
                                      min={0} max={100} step={5}
                                      valueLabelDisplay="auto"
                                      sx={{ color: capColor(cap), py: 0.5 }}
                                    />
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}

        {dateConflicts.length > 0 && (
          <Alert severity="warning">
            ⚠️ Employee is already fully committed (300%) on {dateConflicts.length} date(s) in this range:
            <br />
            {dateConflicts.slice(0, 5).join(", ")}
            {dateConflicts.length > 5 && ` and ${dateConflicts.length - 5} more`}
          </Alert>
        )}
      </Box>
    </form>
  );
}

export default ProjectForm;
